import {
  guruCollectionIdRequestSchema,
  MAX_GURU_COLLECTIONS,
  MAX_GURU_TAG_LENGTH,
  MAX_GURU_TAGS_PER_COLLECTION,
  type GuruIntegrationRequest,
} from "@repo/app-types/entities/integration";
import {
  DEFAULT_KNOWLEDGE_BASE_SYNC_SCHEDULE,
  INITIAL_KNOWLEDGE_BASE_SYNC_AUTOSYNC_ENABLED,
  type GuruKnowledgeBaseSyncIntegration,
  type KnowledgeBaseSyncActiveRun,
} from "@repo/app-types/entities/knowledgeBaseSync";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import type { RecurringSchedule } from "@repo/app-types/scheduling/recurringSchedule";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Checkbox } from "@repo/ui/Checkbox/checkbox";
import { CreatableComboboxField } from "@repo/ui/combobox/creatableComboboxField";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Link } from "@repo/ui/Link/routerLink";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { CenteredModal } from "@repo/ui/Modal/centeredModal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { IconPencil, IconTrash } from "@tabler/icons-react";
import isEqual from "lodash-es/isEqual.js";
import { useEffect, useMemo, useRef, useState, type FC } from "react";
import { Dialog, DialogTrigger } from "react-aria-components";
import { useAllKnowledgeBasesQuery } from "../../../api/entities/knowledgeBase.js";
import {
  getKnowledgeBaseSyncIntegrationErrorMessage,
  useCreateKnowledgeBaseSyncIntegrationMutation,
  useDeleteKnowledgeBaseSyncIntegrationMutation,
  useTriggerKnowledgeBaseSyncMutation,
  useUpdateKnowledgeBaseSyncIntegrationMutation,
} from "../../../api/entities/knowledgeBaseSyncIntegration.js";
import { appPaths } from "../../../app/appPaths.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import { KnowledgeBaseSyncIntegrationScheduleFields } from "./knowledgeBaseSyncIntegrationScheduleFields.js";

interface GuruCollectionEntry {
  collectionId: string;
  collectionToken: string;
  knowledgeBaseName: string;
  tags: string[];
  onlyVerified: boolean;
}

interface ScheduleState {
  syncSchedule: RecurringSchedule;
  autosyncEnabled: boolean;
}

function entriesFromIntegration(
  integration: GuruKnowledgeBaseSyncIntegration | null,
): GuruCollectionEntry[] {
  return (integration?.collections ?? []).map((collection) => ({
    collectionId: collection.collection_id,
    collectionToken: "",
    knowledgeBaseName: collection.knowledge_base_name,
    tags: collection.tags,
    onlyVerified: collection.only_verified,
  }));
}

function collectionIdProblem(
  collectionId: string,
  existingCollectionIds: ReadonlySet<string>,
): string | undefined {
  const trimmedId = collectionId.trim();
  if (!trimmedId) {
    return undefined;
  }
  if (!guruCollectionIdRequestSchema.safeParse(trimmedId).success) {
    return "Not a Guru collection id — expected a UUID.";
  }
  if (existingCollectionIds.has(trimmedId.toLowerCase())) {
    return "This collection is already configured.";
  }
  return undefined;
}

function tagsProblem(tags: readonly string[]): string | undefined {
  if (tags.length > MAX_GURU_TAGS_PER_COLLECTION) {
    return `Add at most ${MAX_GURU_TAGS_PER_COLLECTION} tags.`;
  }
  if (tags.some((tag) => tag.trim().length > MAX_GURU_TAG_LENGTH)) {
    return `Each tag must be ${MAX_GURU_TAG_LENGTH} characters or fewer.`;
  }
  return undefined;
}

function buildGuruRequest(
  entries: readonly GuruCollectionEntry[],
  schedule: ScheduleState,
): GuruIntegrationRequest {
  return {
    collections: entries.map((entry) => ({
      collection_id: entry.collectionId.trim(),
      ...(entry.collectionToken
        ? { collection_token: entry.collectionToken.trim() }
        : {}),
      ...(entry.knowledgeBaseName.trim()
        ? { knowledge_base_name: entry.knowledgeBaseName.trim() }
        : {}),
      tags: entry.tags,
      only_verified: entry.onlyVerified,
    })),
    sync_schedule: schedule.syncSchedule,
    autosync_enabled: schedule.autosyncEnabled,
  };
}

interface AddCollectionDialogContentProps {
  existingCollectionIds: ReadonlySet<string>;
  isSaving: boolean;
  onConnect: (collectionId: string, collectionToken: string) => Promise<void>;
  onClose: () => void;
}

const AddCollectionDialogContent: FC<AddCollectionDialogContentProps> = (
  props,
) => {
  const [collectionId, setCollectionId] = useState("");
  const [collectionToken, setCollectionToken] = useState("");
  const [error, setError] = useState<string | null>(null);
  const idProblem = collectionIdProblem(
    collectionId,
    props.existingCollectionIds,
  );
  const canConnect =
    collectionId.trim().length > 0 &&
    !idProblem &&
    collectionToken.trim().length > 0;

  const handleConnect = async () => {
    setError(null);
    try {
      await props.onConnect(collectionId.trim(), collectionToken.trim());
      props.onClose();
    } catch (connectError) {
      setError(
        connectError instanceof Error
          ? connectError.message
          : "Could not connect the Guru collection — please try again.",
      );
    }
  };

  return (
    <>
      <ModalHeader isCloseVisible>Connect Guru collection</ModalHeader>
      <div className="mt-5 space-y-4">
        <Input
          label="Collection id"
          accessibleLabel="Collection id"
          value={collectionId}
          placeholder="00000000-0000-0000-0000-000000000000"
          errorDescription={idProblem}
          onChange={(event) => setCollectionId(event.target.value)}
          isDisabled={props.isSaving}
        />
        <Input
          label="Collection token"
          accessibleLabel="Collection token"
          type="password"
          autoComplete="off"
          value={collectionToken}
          onChange={(event) => setCollectionToken(event.target.value)}
          isDisabled={props.isSaving}
        />
        {error ? (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        ) : null}
      </div>
      <div className="mt-6 flex justify-end gap-2 border-t border-border-light pt-4">
        <Button
          variant={ButtonVariant.Secondary}
          onClick={props.onClose}
          isDisabled={props.isSaving}
        >
          Cancel
        </Button>
        <Button
          variant={ButtonVariant.Primary}
          onClick={() => void handleConnect()}
          isLoading={props.isSaving}
          isDisabled={!canConnect || props.isSaving}
        >
          Connect
        </Button>
      </div>
    </>
  );
};

interface AddCollectionDialogProps {
  existingCollectionIds: ReadonlySet<string>;
  isDisabled: boolean;
  isSaving: boolean;
  onConnect: (collectionId: string, collectionToken: string) => Promise<void>;
}

const AddCollectionDialog: FC<AddCollectionDialogProps> = (props) => (
  <DialogTrigger>
    <Button variant={ButtonVariant.Secondary} isDisabled={props.isDisabled}>
      Add collection
    </Button>
    <CenteredModal isDismissable size={Size.LARGE}>
      <Dialog className="px-4 pt-5 pb-4 text-left sm:p-6">
        {({ close }) => (
          <AddCollectionDialogContent
            existingCollectionIds={props.existingCollectionIds}
            isSaving={props.isSaving}
            onConnect={props.onConnect}
            onClose={close}
          />
        )}
      </Dialog>
    </CenteredModal>
  </DialogTrigger>
);

interface EditCollectionDialogContentProps {
  entry: GuruCollectionEntry;
  isSaving: boolean;
  onSave: (tags: string[], onlyVerified: boolean) => Promise<void>;
  onClose: () => void;
}

const EditCollectionDialogContent: FC<EditCollectionDialogContentProps> = (
  props,
) => {
  const [tags, setTags] = useState(props.entry.tags);
  const [onlyVerified, setOnlyVerified] = useState(props.entry.onlyVerified);
  const [error, setError] = useState<string | null>(null);
  const tagError = tagsProblem(tags);
  const hasChanges =
    !isEqual(tags, props.entry.tags) ||
    onlyVerified !== props.entry.onlyVerified;

  const handleSave = async () => {
    setError(null);
    try {
      await props.onSave(tags, onlyVerified);
      props.onClose();
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : "Could not update the Guru collection — please try again.",
      );
    }
  };

  return (
    <>
      <ModalHeader isCloseVisible>Edit Guru collection</ModalHeader>
      <div className="mt-5 space-y-5">
        <div className="space-y-1">
          <p className="text-sm font-medium">Collection id</p>
          <p className="font-mono text-sm text-muted-foreground">
            {props.entry.collectionId}
          </p>
        </div>
        <CreatableComboboxField
          label="Tags (optional)"
          value={tags}
          onChange={setTags}
          placeholder="Add a Guru tag name…"
          description="Only include cards that include one of these tags."
          errorMessage={tagError}
          isDisabled={props.isSaving}
        />
        <Checkbox
          label="Verified cards only"
          isChecked={onlyVerified}
          onChange={setOnlyVerified}
          isDisabled={props.isSaving}
        />
        {error ? (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        ) : null}
      </div>
      <div className="mt-6 flex justify-end gap-2 border-t border-border-light pt-4">
        <Button
          variant={ButtonVariant.Secondary}
          onClick={props.onClose}
          isDisabled={props.isSaving}
        >
          Cancel
        </Button>
        <Button
          variant={ButtonVariant.Primary}
          onClick={() => void handleSave()}
          isLoading={props.isSaving}
          isDisabled={!hasChanges || Boolean(tagError) || props.isSaving}
        >
          Save changes
        </Button>
      </div>
    </>
  );
};

interface EditCollectionDialogProps {
  entry: GuruCollectionEntry;
  isDisabled: boolean;
  isSaving: boolean;
  onSave: (tags: string[], onlyVerified: boolean) => Promise<void>;
}

const EditCollectionDialog: FC<EditCollectionDialogProps> = (props) => (
  <DialogTrigger>
    <IconButton
      icon={<IconPencil className="size-4" />}
      variant={ButtonVariant.Secondary}
      accessibleLabel={`Edit ${props.entry.knowledgeBaseName}`}
      isDisabled={props.isDisabled}
    />
    <CenteredModal isDismissable size={Size.LARGE}>
      <Dialog className="px-4 pt-5 pb-4 text-left sm:p-6">
        {({ close }) => (
          <EditCollectionDialogContent
            entry={props.entry}
            isSaving={props.isSaving}
            onSave={props.onSave}
            onClose={close}
          />
        )}
      </Dialog>
    </CenteredModal>
  </DialogTrigger>
);

interface RemoveCollectionDialogContentProps {
  entry: GuruCollectionEntry;
  disconnectsIntegration: boolean;
  isSaving: boolean;
  onRemove: () => Promise<void>;
  onClose: () => void;
}

const RemoveCollectionDialogContent: FC<RemoveCollectionDialogContentProps> = (
  props,
) => {
  const [error, setError] = useState<string | null>(null);

  const handleRemove = async () => {
    setError(null);
    try {
      await props.onRemove();
      props.onClose();
    } catch (removeError) {
      setError(
        removeError instanceof Error
          ? removeError.message
          : "Could not remove the Guru collection — please try again.",
      );
    }
  };

  return (
    <>
      <ModalHeader isCloseVisible>Remove Guru collection?</ModalHeader>
      <p className="mt-4 text-sm text-muted-foreground">
        {props.entry.knowledgeBaseName} will no longer sync from Guru.
        {props.disconnectsIntegration
          ? " Because this is the last collection, Guru will be disconnected."
          : ""}
      </p>
      {error ? (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      <div className="mt-6 flex justify-end gap-2 border-t border-border-light pt-4">
        <Button
          variant={ButtonVariant.Secondary}
          onClick={props.onClose}
          isDisabled={props.isSaving}
        >
          Cancel
        </Button>
        <Button
          variant={ButtonVariant.Warning}
          onClick={() => void handleRemove()}
          isLoading={props.isSaving}
          isDisabled={props.isSaving}
        >
          Remove collection
        </Button>
      </div>
    </>
  );
};

interface RemoveCollectionDialogProps {
  entry: GuruCollectionEntry;
  disconnectsIntegration: boolean;
  isDisabled: boolean;
  isSaving: boolean;
  onRemove: () => Promise<void>;
}

const RemoveCollectionDialog: FC<RemoveCollectionDialogProps> = (props) => (
  <DialogTrigger>
    <IconButton
      icon={<IconTrash className="size-4" />}
      variant={ButtonVariant.Secondary}
      accessibleLabel={`Remove ${props.entry.knowledgeBaseName}`}
      isDisabled={props.isDisabled}
    />
    <CenteredModal isDismissable size={Size.LARGE}>
      <Dialog className="px-4 pt-5 pb-4 text-left sm:p-6">
        {({ close }) => (
          <RemoveCollectionDialogContent
            entry={props.entry}
            disconnectsIntegration={props.disconnectsIntegration}
            isSaving={props.isSaving}
            onRemove={props.onRemove}
            onClose={close}
          />
        )}
      </Dialog>
    </CenteredModal>
  </DialogTrigger>
);

export interface GuruIntegrationSettingsProps {
  integration: GuruKnowledgeBaseSyncIntegration | null;
  activeSyncRun: KnowledgeBaseSyncActiveRun | null;
  isLoading: boolean;
  isError: boolean;
  onReload: () => void;
}

export const GuruIntegrationSettings: FC<GuruIntegrationSettingsProps> = (
  props,
) => {
  const { integration, activeSyncRun, isLoading, isError, onReload } = props;
  const [createIntegration, createState] =
    useCreateKnowledgeBaseSyncIntegrationMutation();
  const [updateIntegration, updateState] =
    useUpdateKnowledgeBaseSyncIntegrationMutation();
  const [deleteIntegration, deleteState] =
    useDeleteKnowledgeBaseSyncIntegrationMutation();
  const [triggerSync, triggerState] = useTriggerKnowledgeBaseSyncMutation();
  const isSaving =
    createState.isLoading || updateState.isLoading || deleteState.isLoading;
  const { data: knowledgeBases, refetch: refetchKnowledgeBases } =
    useAllKnowledgeBasesQuery();
  const [entries, setEntries] = useState<GuruCollectionEntry[]>(() =>
    entriesFromIntegration(integration),
  );
  const [integrationId, setIntegrationId] = useState(
    integration?.integration_id ?? null,
  );
  const [schedule, setSchedule] = useState<ScheduleState>(() => ({
    syncSchedule:
      integration?.sync_schedule ?? DEFAULT_KNOWLEDGE_BASE_SYNC_SCHEDULE,
    autosyncEnabled:
      integration?.autosync_enabled ??
      INITIAL_KNOWLEDGE_BASE_SYNC_AUTOSYNC_ENABLED,
  }));
  const [saveError, setSaveError] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const hadActiveSyncRunRef = useRef(false);

  const initialEntries = useMemo(
    () => entriesFromIntegration(integration),
    [integration],
  );
  const initialSchedule = useMemo<ScheduleState>(
    () => ({
      syncSchedule:
        integration?.sync_schedule ?? DEFAULT_KNOWLEDGE_BASE_SYNC_SCHEDULE,
      autosyncEnabled:
        integration?.autosync_enabled ??
        INITIAL_KNOWLEDGE_BASE_SYNC_AUTOSYNC_ENABLED,
    }),
    [integration],
  );
  const existingCollectionIds = useMemo(
    () => new Set(entries.map((entry) => entry.collectionId.toLowerCase())),
    [entries],
  );
  const syncedKnowledgeBaseByCollectionId = useMemo(
    () =>
      new Map(
        (knowledgeBases ?? []).flatMap((knowledgeBase) => {
          const externalProvider = knowledgeBase.metadata?.external_provider;
          return externalProvider?.provider === "guru"
            ? [[externalProvider.external_id, knowledgeBase] as const]
            : [];
        }),
      ),
    [knowledgeBases],
  );

  useEffect(() => {
    setEntries(initialEntries);
    setIntegrationId(integration?.integration_id ?? null);
    setSchedule(initialSchedule);
  }, [initialEntries, initialSchedule, integration?.integration_id]);
  useEffect(() => {
    if (activeSyncRun) {
      hadActiveSyncRunRef.current = true;
      return;
    }
    if (hadActiveSyncRunRef.current) {
      hadActiveSyncRunRef.current = false;
      void refetchKnowledgeBases();
    }
  }, [activeSyncRun, refetchKnowledgeBases]);

  const persistEntries = async (
    nextEntries: GuruCollectionEntry[],
    nextSchedule: ScheduleState,
  ) => {
    try {
      const request = buildGuruRequest(nextEntries, nextSchedule);
      const savedIntegration = integrationId
        ? await updateIntegration({
            provider: "guru",
            integrationId,
            configuration: request,
          }).unwrap()
        : await createIntegration({
            provider: "guru",
            configuration: request,
          }).unwrap();
      if (savedIntegration.provider !== "guru") {
        throw new Error("Guru returned an invalid integration response.");
      }
      setIntegrationId(savedIntegration.integration_id);
      setEntries(entriesFromIntegration(savedIntegration));
      setSchedule({
        syncSchedule: savedIntegration.sync_schedule,
        autosyncEnabled: savedIntegration.autosync_enabled,
      });
    } catch (error) {
      throw new Error(
        getKnowledgeBaseSyncIntegrationErrorMessage(
          error,
          "Could not save the Guru configuration — please try again.",
        ),
      );
    }
  };

  const handleAddCollection = async (
    collectionId: string,
    collectionToken: string,
  ) => {
    await persistEntries(
      [
        ...entries,
        {
          collectionId,
          collectionToken,
          knowledgeBaseName: "",
          tags: [],
          onlyVerified: false,
        },
      ],
      initialSchedule,
    );
  };

  const handleEditCollection = async (
    collectionId: string,
    tags: string[],
    onlyVerified: boolean,
  ) => {
    await persistEntries(
      entries.map((entry) =>
        entry.collectionId === collectionId
          ? { ...entry, tags, onlyVerified }
          : entry,
      ),
      initialSchedule,
    );
  };

  const handleRemoveCollection = async (collectionId: string) => {
    if (entries.length === 1 && integrationId) {
      try {
        await deleteIntegration({
          provider: "guru",
          integrationId,
        }).unwrap();
        setIntegrationId(null);
        setEntries([]);
        setSchedule({
          syncSchedule: DEFAULT_KNOWLEDGE_BASE_SYNC_SCHEDULE,
          autosyncEnabled: INITIAL_KNOWLEDGE_BASE_SYNC_AUTOSYNC_ENABLED,
        });
        return;
      } catch (error) {
        throw new Error(
          getKnowledgeBaseSyncIntegrationErrorMessage(
            error,
            "Could not disconnect Guru — please try again.",
          ),
        );
      }
    }

    await persistEntries(
      entries.filter((entry) => entry.collectionId !== collectionId),
      initialSchedule,
    );
  };

  const handleScheduleSave = async () => {
    setSaveError(null);
    try {
      await persistEntries(entries, schedule);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : String(error));
    }
  };

  const handleSyncNow = async () => {
    if (!integrationId) return;
    setSyncError(null);
    try {
      await triggerSync({
        provider: "guru",
        integrationId,
      }).unwrap();
    } catch (error) {
      setSyncError(
        getKnowledgeBaseSyncIntegrationErrorMessage(
          error,
          "Could not start the Guru sync — please try again.",
        ),
      );
    }
  };

  if (isLoading) {
    return <Loader style={LoaderStyle.SPINNER} />;
  }
  if (isError) {
    return (
      <div className="flex max-w-3xl flex-col items-start gap-3 rounded-lg border border-border-light bg-card p-4">
        <p className="text-sm text-muted-foreground">
          Couldn&apos;t load your Guru configuration.
        </p>
        <Button
          variant={ButtonVariant.Secondary}
          size={Size.SMALL}
          onClick={onReload}
        >
          Try again
        </Button>
      </div>
    );
  }

  const requiredAction = integrationId
    ? PermissionActions.UPDATE
    : PermissionActions.CREATE;
  const hasScheduleChanges = !isEqual(schedule, initialSchedule);

  return (
    <Can
      I={requiredAction}
      a={PermissionSubjects.INTEGRATIONS}
      passThrough={true}
    >
      {(allowed: boolean) => {
        const areWritesDisabled = !allowed || isSaving;
        const areCollectionWritesDisabled =
          areWritesDisabled || hasScheduleChanges;

        return (
          <div className="max-w-3xl space-y-5">
            {!allowed ? (
              <p className="text-sm text-muted-foreground">
                You don&apos;t have permission to change this integration.
              </p>
            ) : null}

            {entries.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No collections configured yet.
              </p>
            ) : (
              <ul className="space-y-3">
                {entries.map((entry) => {
                  const syncedKnowledgeBase =
                    syncedKnowledgeBaseByCollectionId.get(entry.collectionId);
                  return (
                    <li
                      key={entry.collectionId}
                      className="flex items-center gap-3 rounded-md border border-border-light bg-card p-4"
                    >
                      <div className="min-w-0 flex-1">
                        {integrationId && syncedKnowledgeBase ? (
                          <Link
                            href={appPaths.settingsKnowledgeBase.build({
                              knowledgeBaseId: syncedKnowledgeBase.id,
                            })}
                            className="truncate text-sm font-medium text-indigo-600 hover:underline"
                          >
                            {syncedKnowledgeBase.name}
                          </Link>
                        ) : (
                          <p className="truncate text-sm font-medium">
                            {entry.knowledgeBaseName || entry.collectionId}
                          </p>
                        )}
                        <p className="mt-1 truncate font-mono text-xs text-muted-foreground">
                          {entry.collectionId}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <EditCollectionDialog
                          entry={entry}
                          isDisabled={areCollectionWritesDisabled}
                          isSaving={isSaving}
                          onSave={(tags, onlyVerified) =>
                            handleEditCollection(
                              entry.collectionId,
                              tags,
                              onlyVerified,
                            )
                          }
                        />
                        <Can
                          I={PermissionActions.DELETE}
                          a={PermissionSubjects.INTEGRATIONS}
                          passThrough={true}
                        >
                          {(canDisconnect) => (
                            <RemoveCollectionDialog
                              entry={entry}
                              disconnectsIntegration={entries.length === 1}
                              isDisabled={
                                areCollectionWritesDisabled ||
                                (entries.length === 1 && !canDisconnect)
                              }
                              isSaving={isSaving}
                              onRemove={() =>
                                handleRemoveCollection(entry.collectionId)
                              }
                            />
                          )}
                        </Can>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            <AddCollectionDialog
              existingCollectionIds={existingCollectionIds}
              isDisabled={
                areCollectionWritesDisabled ||
                entries.length >= MAX_GURU_COLLECTIONS
              }
              isSaving={isSaving}
              onConnect={handleAddCollection}
            />

            {integrationId ? (
              <>
                <KnowledgeBaseSyncIntegrationScheduleFields
                  syncSchedule={schedule.syncSchedule}
                  autosyncEnabled={schedule.autosyncEnabled}
                  onScheduleChange={(syncSchedule) =>
                    setSchedule((current) => ({ ...current, syncSchedule }))
                  }
                  onAutosyncEnabledChange={(autosyncEnabled) =>
                    setSchedule((current) => ({
                      ...current,
                      autosyncEnabled,
                    }))
                  }
                  isDisabled={areWritesDisabled}
                />
                <Can
                  I={PermissionActions.CREATE}
                  a={PermissionSubjects.KNOWLEDGE_BASES}
                  passThrough={true}
                >
                  {(canSync) => (
                    <Button
                      variant={ButtonVariant.Secondary}
                      onClick={() => void handleSyncNow()}
                      isLoading={triggerState.isLoading}
                      isDisabled={
                        !canSync ||
                        triggerState.isLoading ||
                        activeSyncRun !== null ||
                        hasScheduleChanges
                      }
                    >
                      Sync now
                    </Button>
                  )}
                </Can>
                {activeSyncRun ? (
                  <p className="text-sm text-muted-foreground" role="status">
                    {activeSyncRun.status === "running"
                      ? "Sync in progress."
                      : "Sync scheduled."}
                  </p>
                ) : null}
                {hasScheduleChanges ? (
                  <p className="text-sm text-muted-foreground">
                    Save schedule changes before managing collections or
                    syncing.
                  </p>
                ) : null}
              </>
            ) : null}

            {saveError ? (
              <p className="text-sm text-red-600" role="alert">
                {saveError}
              </p>
            ) : null}
            {syncError ? (
              <p className="text-sm text-red-600" role="alert">
                {syncError}
              </p>
            ) : null}

            {allowed && integrationId && hasScheduleChanges ? (
              <div className="flex items-center gap-2">
                <Button
                  variant={ButtonVariant.Primary}
                  onClick={() => void handleScheduleSave()}
                  isLoading={isSaving}
                  isDisabled={isSaving}
                >
                  Save
                </Button>
                <Button
                  variant={ButtonVariant.Secondary}
                  onClick={() => {
                    setSchedule(initialSchedule);
                    setSaveError(null);
                  }}
                  isDisabled={isSaving}
                >
                  Cancel
                </Button>
              </div>
            ) : null}
          </div>
        );
      }}
    </Can>
  );
};
